import { FC } from 'react';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { toast } from 'react-toastify';
import { PermissionRole } from '@magicbrief/prisma/generated/client2';
import { trpc } from 'src/lib/trpc';
import { useI18nContext } from 'src/i18n/i18n-react';
import AriaModal from 'src/components/AriaModal/AriaModal';
import { AriaButton } from 'src/components/Button/Button';
import Alert from 'src/components/AlertV2';
import { Icon } from 'src/components/Icon';
import AlertTriangle from 'src/assets/svgicons/duotone/alert-triangle.svg';
import { useUserAndOrganisation } from 'src/utils/useUserAndOrganisation';

type Props = {
  uuid: string;
  name: string;
  newRole?: PermissionRole;
  show: boolean;
  onClose: () => void;
};

export const UpgradeGuestRoleModal: FC<Props> = ({
  uuid,
  name,
  newRole = 'editor',
  show,
  onClose,
}) => {
  const { LL } = useI18nContext();
  const queryClient = useQueryClient();

  const userAndOrg = useUserAndOrganisation();

  const upgradeUserMutation =
    trpc.organisation.upgradeGuestToEditor.useMutation({
      onSuccess: () => {
        void queryClient.invalidateQueries(
          getQueryKey(trpc.organisation.getGuestUsers)
        );
        void queryClient.invalidateQueries(getQueryKey(trpc.permissions));
        toast.success(
          LL.workspaceSettings.upgradeModal.userUpgradeSuccess({ name }),
          {
            className: 'toast-success',
          }
        );
        onClose();
      },
      onError: (error) => {
        captureException(error, (scope) => {
          scope.setTransactionName('Upgrade Guest');
          return scope;
        });
        toast.error(error.message, {
          className: 'toast-error',
        });
      },
    });

  const onConfirm = () => {
    userAndOrg.data && upgradeUserMutation.mutate({ uuid, role: newRole });
  };

  return (
    <AriaModal
      show={show}
      onClose={onClose}
      title={
        <span className="whitespace-normal text-xl">
          {LL.workspaceSettings.upgradeModal.changeGuestRole({
            name,
            role: newRole,
          })}
        </span>
      }
      showCloseButton={false}
      modalBodyClassName="mt-[2px]"
      footer={
        <div className="mt-2 flex w-full flex-row items-center justify-end gap-3">
          <AriaButton
            variant="white"
            onPress={onClose}
            isDisabled={false}
            className=""
          >
            {LL.workspaceSettings.upgradeModal.cancel()}
          </AriaButton>
          <AriaButton
            variant="primary"
            onPress={onConfirm}
            loading={false}
            className=""
          >
            {LL.workspaceSettings.upgradeModal.upgradeUser()}
          </AriaButton>
        </div>
      }
    >
      <div className="flex flex-col items-center justify-center gap-4">
        <p className="text-sm font-medium text-[#917DD2]">
          {LL.workspaceSettings.upgradeModal.userWillHaveAccess()}
        </p>
        <div className="flex flex-row items-start justify-center">
          <Alert
            variant="warning"
            className="flex flex-row items-start rounded-md border-none px-3 py-4 text-xs font-medium text-yellow-700"
            icon={
              <Icon className="mt-1.5 min-w-5">
                <AlertTriangle />
              </Icon>
            }
          >
            <span className="text-xs font-medium">
              {LL.workspaceSettings.upgradeModal.previousGuestPermission()}
            </span>
          </Alert>
        </div>
      </div>
    </AriaModal>
  );
};
